import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Circle,
  Mail,
  Sparkles,
  Calendar,
  Link2,
  TrendingUp,
  ArrowUp,
  Play,
} from "lucide-react";

/**
 * Premium "product screen" demo block. Renders a Mac-style browser chrome
 * frame with an animated step-specific mock of the actual Studio surface.
 * Looks like a real product capture - no stock photography, no "coming soon".
 *
 * Drop in `embedUrl` to upgrade any slot to a real Loom/YouTube video later.
 *
 * Scenes available: signup · builder · launch · insights
 */
export default function ProductScreen({
  scene = "signup",
  embedUrl = null,
  caption = null,
  url = "goldkey.studio",
  testid = "product-screen",
}) {
  const [playing, setPlaying] = useState(false);
  const Scene = SCENES[scene] || SCENES.signup;

  return (
    <figure
      data-testid={testid}
      className="relative w-full overflow-hidden border border-neutral-200 bg-white shadow-2xl"
    >
      {/* macOS browser chrome */}
      <div className="flex items-center gap-2 px-3 sm:px-4 py-3 border-b border-neutral-200 bg-white/80">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[10px] sm:text-xs text-neutral-400 tracking-wide bg-[#FAFAFA] border border-neutral-200/70 px-3 py-1 rounded-sm font-mono">
            {url}
          </span>
        </div>
      </div>

      {/* Screen content */}
      <div className="relative aspect-[16/10] bg-[#F7F5EF]">
        {embedUrl && playing ? (
          <iframe
            src={embedUrl + (embedUrl.includes("?") ? "&autoplay=1" : "?autoplay=1")}
            title={caption || "Demo video"}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <>
            <Scene />
            {embedUrl && (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label="Play demo"
                data-testid={`${testid}-play`}
                className="absolute inset-0 flex items-center justify-center bg-[#F7F5EF]/40 backdrop-blur-[2px] hover:bg-[#F7F5EF]/20 transition"
              >
                <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-gold/60 bg-gold/15 flex items-center justify-center">
                  <Play size={20} className="text-gold ml-1" strokeWidth={1.8} />
                </span>
              </button>
            )}
          </>
        )}
      </div>

      {caption && (
        <figcaption className="px-4 sm:px-5 py-3 sm:py-4 text-xs text-neutral-500 border-t border-neutral-200 bg-white">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/* ─── Scene primitives ─────────────────────────────────────────────────── */

function Field({ label, value, focused = false, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, margin: "-40px" }}
      transition={{ duration: 0.5, delay }}
      className="space-y-1"
    >
      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-neutral-500">{label}</div>
      <div
        className={`text-xs sm:text-sm px-3 py-2 border ${
          focused ? "border-gold/60 bg-gold/[0.04]" : "border-neutral-200 bg-white"
        } text-neutral-900`}
      >
        {value}
        {focused && (
          <span className="inline-block w-px h-3 bg-gold ml-0.5 align-middle animate-pulse" />
        )}
      </div>
    </motion.div>
  );
}

/* SIGNUP - fills out the trial form */
function SignupScene() {
  return (
    <div className="absolute inset-0 p-5 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
      <div className="hidden md:block">
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold/80">Pick your plan in 60 seconds</p>
        <h4 className="font-serif text-2xl lg:text-3xl text-neutral-900 mt-3 leading-tight">
          One <span className="italic text-aurora">GoldKey</span>, two plans.
        </h4>
        <ul className="mt-5 space-y-2 text-xs text-neutral-500">
          {[
            "Starter · $49/mo · 10k credits",
            "Growth · $199/mo · unlimited",
            "Cancel anytime · annual saves 20%",
          ].map((t) => (
            <li key={t} className="flex items-center gap-2">
              <CheckCircle2 size={12} className="text-gold flex-none" />
              {t}
            </li>
          ))}
        </ul>
      </div>
      <div className="space-y-3 max-w-[300px] w-full mx-auto">
        <Field label="Your name" value="Carla Reyes" delay={0.1} />
        <Field label="Work email" value="carla@lumi-skin.com" delay={0.25} />
        <Field label="Plan" value="Starter · $49 / month" focused delay={0.4} />
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 0.5, delay: 0.55 }}
          className="bg-aurora text-white text-[10px] sm:text-xs uppercase tracking-[0.2em] py-2.5 text-center font-medium flex items-center justify-center gap-2 rounded-sm"
        >
          Pay with Stripe <ArrowRight size={12} />
        </motion.div>
      </div>
    </div>
  );
}

/* BUILDER - chat thread with a real-feeling prompt + a "shipped artefact" card */
function BuilderScene() {
  const messages = [
    { who: "you", text: "I want a 4-week Instagram plan promoting Botox specials in Brookline, plus a booking form for new clients." },
    { who: "studio", text: "Got it. Three quick questions to match your voice:" },
    { who: "studio", text: "1) Tone - warm-clinical or playful-luxe? 2) Best offer to lead with? 3) One existing post you'd point me to?" },
    { who: "you", text: "Warm-clinical. $99 first Botox. Latest reel." },
    { who: "studio", text: "Building. 12 posts, 1 booking form, 1 SMS follow-up." },
  ];
  const artefacts = [
    { label: "Content plan", meta: "12 posts · IG + Email", chip: "Ready" },
    { label: "Booking form", meta: "/lumi/book · 4 fields", chip: "Live" },
    { label: "SMS follow-up", meta: "Day 1 · Day 3 · Day 7", chip: "Queued" },
  ];
  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-200/70 bg-white">
        <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Studio Builder · Lumi Skin Studio</span>
        <Sparkles size={11} className="ml-auto text-gold" />
      </div>
      <div className="flex-1 overflow-hidden p-4 sm:p-6 space-y-3">
        {messages.map((m, i) => (
          <motion.div
            key={`${m.who}-${i}-${m.text.slice(0, 12)}`}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, margin: "-20px" }}
            transition={{ duration: 0.45, delay: 0.1 + i * 0.15 }}
            className={`flex ${m.who === "you" ? "justify-end" : "justify-start"}`}
          >
            <span
              className={`max-w-[78%] text-[11px] sm:text-xs leading-relaxed px-3 py-2 ${
                m.who === "you"
                  ? "bg-gold text-[#1a1206]"
                  : "bg-[#FAFAFA] border border-neutral-200 text-neutral-700"
              }`}
            >
              {m.text}
            </span>
          </motion.div>
        ))}
        {/* Shipped artefact card - the proof-of-output */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-20px" }}
          transition={{ duration: 0.55, delay: 0.95 }}
          className="mt-2 border border-gold/30 bg-gold/[0.04] rounded-sm p-3 sm:p-4"
        >
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-gold">Your studio · ready in 18 min</p>
            <span className="text-[9px] uppercase tracking-[0.2em] text-emerald-600 flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-emerald-500" /> Shipped
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
            {artefacts.map((a, i) => (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ duration: 0.4, delay: 1.05 + i * 0.08 }}
                className="border border-neutral-200 bg-white/70 px-2.5 py-2 rounded-sm"
              >
                <p className="text-[10px] text-neutral-900 font-medium truncate">{a.label}</p>
                <p className="text-[9px] text-neutral-500 truncate mt-0.5">{a.meta}</p>
                <p className="text-[8px] uppercase tracking-[0.15em] text-gold mt-1.5">{a.chip}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-neutral-200 bg-white">
        <span className="flex-1 text-[11px] text-neutral-400 px-2">Tell the Studio what you need next…</span>
        <span className="w-7 h-7 bg-gold text-[#1a1206] flex items-center justify-center">
          <ArrowUp size={12} strokeWidth={2.2} />
        </span>
      </div>
    </div>
  );
}

/* LAUNCH - calendar grid filling in + a "Live" toast */
function LaunchScene() {
  const days = Array.from({ length: 28 }, (_, i) => i);
  // Channel mix per filled day so users see real "what's scheduled"
  const filled = new Map([
    [1, { ch: "ig", label: "Reel" }],
    [3, { ch: "li", label: "Post" }],
    [5, { ch: "em", label: "Email" }],
    [8, { ch: "ig", label: "Carousel" }],
    [10, { ch: "tt", label: "TikTok" }],
    [12, { ch: "li", label: "Post" }],
    [15, { ch: "em", label: "Email" }],
    [17, { ch: "ig", label: "Story" }],
    [19, { ch: "li", label: "Post" }],
    [22, { ch: "ig", label: "Reel" }],
    [24, { ch: "em", label: "Email" }],
    [26, { ch: "tt", label: "TikTok" }],
  ]);
  const channelDot = {
    ig: "bg-pink-400",
    li: "bg-sky-400",
    em: "bg-gold",
    tt: "bg-fuchsia-400",
  };

  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-gold/80">November · Content calendar</p>
          <p className="font-serif text-base sm:text-lg text-neutral-900 mt-1">Lumi Skin Studio</p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-600 border border-emerald-500/40 px-3 py-1 bg-emerald-50"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live
        </motion.div>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 flex-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={`wd-${i}`} className="text-[9px] uppercase tracking-[0.18em] text-neutral-400 text-center pb-1">
            {d}
          </div>
        ))}
        {days.map((_, i) => {
          const entry = filled.get(i);
          return (
            <motion.div
              key={`day-${i}`}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: false }}
              transition={{ duration: 0.25, delay: 0.05 + i * 0.015 }}
              className={`aspect-square flex flex-col p-1 sm:p-1.5 text-[8px] sm:text-[9px] relative ${
                entry
                  ? "bg-gold/12 border border-gold/40 text-neutral-900"
                  : "bg-white border border-neutral-200/70 text-neutral-400"
              }`}
            >
              {entry && (
                <div className="flex items-center gap-1 mb-auto">
                  <span className={`w-1.5 h-1.5 rounded-full ${channelDot[entry.ch]} flex-none`} />
                  <span className="text-[7px] sm:text-[8px] uppercase tracking-[0.1em] text-neutral-600 truncate">
                    {entry.label}
                  </span>
                </div>
              )}
              <span className={`ml-auto ${entry ? "text-neutral-700" : "text-neutral-400"}`}>
                {i + 1}
              </span>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="mt-4 grid grid-cols-2 gap-2 text-[10px]"
      >
        <div className="border border-neutral-200 px-3 py-2 bg-white flex items-center gap-2">
          <Link2 size={11} className="text-gold flex-none" />
          <span className="text-neutral-500 truncate font-mono">goldkey.studio/lumi/book</span>
        </div>
        <div className="border border-neutral-200 px-3 py-2 bg-white flex items-center gap-2 text-neutral-500">
          <Calendar size={11} className="text-gold flex-none" />
          12 posts queued · 4 weeks
        </div>
      </motion.div>
    </div>
  );
}

/* INSIGHTS - Friday morning recap email */
function InsightsScene() {
  return (
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mail size={12} className="text-gold" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Inbox · Friday 8:02am</span>
        </div>
        <span className="text-[10px] text-neutral-400">GoldKey Studio</span>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="border border-neutral-200 bg-white p-4 sm:p-5 flex-1 flex flex-col"
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold/80">Your weekly Success Insights</p>
        <h5 className="font-serif text-lg sm:text-xl text-neutral-900 mt-2 leading-tight">
          Hi Carla - here&apos;s what changed last week.
        </h5>

        <div className="grid grid-cols-2 gap-3 mt-4">
          {[
            { k: "Posts published", v: "12", trend: "+12 vs prior" },
            { k: "Lead form fills", v: "9", trend: "from 0 the week before" },
          ].map((s, i) => (
            <motion.div
              key={s.k}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.15 }}
              className="border border-gold/20 bg-gold/[0.04] px-3 py-3"
            >
              <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500">{s.k}</div>
              <div className="font-serif text-xl sm:text-2xl text-gold leading-none mt-1">{s.v}</div>
              <div className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1">
                <TrendingUp size={10} /> {s.trend}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: false }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="text-[11px] text-neutral-500 leading-relaxed mt-4 flex-1"
        >
          The 9 form fills are new - none of those names existed in your inbox a week ago. <span className="text-gold">One clear next step:</span> reply to Maria H. and Aisha K. - both viewed your link twice.
        </motion.p>
      </motion.div>
    </div>
  );
}

const SCENES = {
  signup: SignupScene,
  builder: BuilderScene,
  launch: LaunchScene,
  insights: InsightsScene,
};
